"use client";

import { useTranslation } from "next-i18next";
import { IProductInfo } from "@/props/IProduct";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import { useState } from "react";
import { useRouter } from "next/router";
import debounce from "lodash/debounce";
import { Spin, message } from "antd";
import { HeartOutlined, HeartFilled } from "@ant-design/icons";
import { addFavoriteApi, batchRemoveFavoriteApi } from "@/api/favorites";
import { onKeyActivate } from "@/utils/index";

/**
 * product favorite button component
 * @param product product info
 * @param isMobile is or not mobile (icon-only mode, no label)
 */
const ProductFavorite = ({
  product,
  isMobile = false,
}: {
  product?: IProductInfo;
  isMobile?: boolean;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const userInfo = useAppSelector((state: RootState) => state.user.userInfo);
  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const [favoriteLoading, setFavoriteLoading] = useState<boolean>(false);

  const handleToggleFavorite = async () => {
    if (!userInfo) {
      return router.push(`/login?redirect=${router.asPath}`);
    }
    if (!product) return;
    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        await batchRemoveFavoriteApi({ ids: [product.id] });
        setIsFavorited(false);
        message.success(t("favorites.remove_success"));
      } else {
        await addFavoriteApi({ productId: product.id });
        setIsFavorited(true);
        message.success(t("favorites.favorite_success"));
      }
    } catch (err) {
      console.log("Failed:", err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  if (isMobile) {
    // mobile: icon only, for top-right header area
    return (
      <div
        className="flex h-full cursor-pointer items-center justify-center px-2"
        onClick={debounce(handleToggleFavorite, 300)}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyActivate(() => handleToggleFavorite())}
      >
        {favoriteLoading ? (
          <Spin size="small" />
        ) : isFavorited ? (
          <HeartFilled className="text-xl" />
        ) : (
          <HeartOutlined className="text-xl" />
        )}
      </div>
    );
  }

  // PC: icon + label
  return (
    <div
      className="ml-4 flex cursor-pointer flex-row items-center justify-center transition-opacity hover:opacity-70"
      onClick={debounce(handleToggleFavorite, 300)}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyActivate(() => handleToggleFavorite())}
      data-qt="mall_detail_favorite_click"
      data-qt-goods-id={product?.id}
      data-qt-goods-name={product?.storeName}
      data-qt-favorited={isFavorited ? 1 : 0}
    >
      {favoriteLoading ? (
        <Spin size="small" />
      ) : isFavorited ? (
        <HeartFilled className="text-xl" />
      ) : (
        <HeartOutlined className="text-xl" />
      )}
      <span className="ml-1 text-xs">{t("favorites.favorite")}</span>
    </div>
  );
};

export default ProductFavorite;
